import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fieldError, type ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDateTime } from '../../lib/format';
import type { WaitlistEntry, WaitlistStatus } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  Modal,
  ModuleNotIncluded,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';
import { useClientPackage } from '../../lib/clientPackage';

const STATUS_TONE: Record<WaitlistStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  waiting: 'neutral',
  notified: 'warning',
  purchased: 'success',
  expired: 'danger',
};

export function WaitlistPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const { hasModule, isLoading: pkgLoading } = useClientPackage();

  const query = useQuery({
    queryKey: ['waitlist', statusFilter],
    queryFn: () => api.waitlist.list({ status: statusFilter || undefined, limit: 100 }),
  });

  // docs/ARCHITECTURE_V2.md §3.4/§12 agent 7: courtesy-only gate, see PromoCodesPage.tsx's
  // identical comment. Module key is `waitlists` (plural) per §6's schema, this page is
  // `/waitlist` (singular) - intentional, matches the server's own naming.
  if (!pkgLoading && !hasModule('waitlists')) {
    return (
      <div>
        <PageHeader title="Waitlist" />
        <ModuleNotIncluded feature="Waitlist" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Waitlist"
        description="Buyers waiting for sold-out sessions or ticket types."
        actions={
          <RequirePermission permission="waitlists.manage">
            <Button onClick={() => setModalOpen(true)}>Add entry</Button>
          </RequirePermission>
        }
      />
      <Card className="mb-6">
        <FormField label="Filter by status">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="waiting">Waiting</option>
            <option value="notified">Notified</option>
            <option value="purchased">Purchased</option>
            <option value="expired">Expired</option>
          </Select>
        </FormField>
      </Card>
      {query.isLoading ? (
        <Spinner />
      ) : query.error ? (
        <ErrorSummary error={toParsedError(query.error)} />
      ) : (
        <DataTable<WaitlistEntry>
          columns={[
            { key: 'email', header: 'Buyer email', render: (w) => w.buyerEmail },
            { key: 'phone', header: 'Phone', render: (w) => w.buyerPhone ?? '-' },
            {
              key: 'status',
              header: 'Status',
              render: (w) => <Badge tone={STATUS_TONE[w.status]}>{w.status}</Badge>,
            },
            { key: 'notified', header: 'Notified at', render: (w) => formatDateTime(w.notifiedAt) },
            {
              key: 'expires',
              header: 'Purchase link expires',
              render: (w) => formatDateTime(w.purchaseLinkExpiresAt),
            },
          ]}
          rows={query.data?.data ?? []}
          getRowKey={(w) => w.id}
          emptyMessage="Nobody is on the waitlist."
        />
      )}
      <AddWaitlistEntryModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
}

function AddWaitlistEntryModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [eventId, setEventId] = useState('');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerPhone, setBuyerPhone] = useState('');
  const [formError, setFormError] = useState<ParsedApiError | null>(null);

  const eventsQuery = useQuery({
    queryKey: ['events', 'picker'],
    queryFn: () => api.events.list({ limit: 100 }),
  });

  const mutation = useMutation({
    // REAL BUG FOUND AND FIXED: createWaitlistEntrySchema's buyerPhone is `.optional()`
    // (apps/api/src/routes/management/schemas.ts), which rejects an explicit `null` - every entry
    // added with no phone number 400'd. Also dropped `status: 'waiting'`, which the schema doesn't
    // declare at all (dead field, silently stripped by zod - not an error, just never did
    // anything).
    mutationFn: () =>
      api.waitlist.create({
        eventId,
        buyerEmail,
        ...(buyerPhone ? { buyerPhone } : {}),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      onClose();
    },
    onError: (err) => setFormError(toParsedError(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add waitlist entry"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!eventId || !buyerEmail.trim() || mutation.isPending}
          >
            {mutation.isPending ? 'Adding...' : 'Add'}
          </Button>
        </>
      }
    >
      {formError && <ErrorSummary error={formError} className="mb-3" />}
      <FormField label="Event" error={fieldError(formError, 'eventId')}>
        <Select value={eventId} onChange={(e) => setEventId(e.target.value)}>
          <option value="">Select an event...</option>
          {eventsQuery.data?.data.map((ev) => (
            <option key={ev.id} value={ev.id}>
              {ev.title}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label="Buyer email" error={fieldError(formError, 'buyerEmail')}>
        <Input type="email" value={buyerEmail} onChange={(e) => setBuyerEmail(e.target.value)} />
      </FormField>
      <FormField label="Buyer phone" hint="Optional" error={fieldError(formError, 'buyerPhone')}>
        <Input value={buyerPhone} onChange={(e) => setBuyerPhone(e.target.value)} />
      </FormField>
    </Modal>
  );
}
